@if (pageData$ | async; as pageData) {
  @if (pageData.quote && pageData.revision) {
    <p-card
      class="@container bg-white rounded-xl border border-surface-switch-300 overflow-hidden"
    >
      <ng-template #header>
        <div class="flex justify-between items-center p-5 pt-4 pb-0">
          <div class="font-semibold text-xl">Summary</div>
        </div>
      </ng-template>

      <div class="px-0">
        <div class="flex flex-col gap-3">
          <div
            class="grid grid-cols-1 @lg:grid-cols-2 @3xl:grid-cols-3 @5xl:grid-cols-4 gap-6"
          >
            <div>
              <div class="text-xs text-surface-switch-600 mb-2">Number</div>
              <div class="text-sm font-medium text-surface-switch-900">
                {{ pageData.quote.code_number }}
              </div>
            </div>

            <div>
              <div class="flex items-center gap-2 mb-2">
                <div class="text-xs text-surface-switch-600 font-semibold">
                  Reference
                </div>
                @if (!isEditingReference) {
                  <button
                    pButton
                    [text]="true"
                    size="small"
                    severity="secondary"
                    [disabled]="readOnly"
                    (click)="startEditingReference(pageData.quote)"
                  >
                    <i class="pi pi-pencil w-3 h-3"></i>
                  </button>
                }
              </div>

              @if (isEditingReference) {
                <div class="mt-2 w-full">
                  <input
                    type="text"
                    pInputText
                    class="w-full text-sm text-surface-switch-900"
                    [(ngModel)]="referenceDraft"
                    [disabled]="isSavingReference"
                    placeholder="Add a reference"
                  />
                  <div class="flex gap-2 mt-2">
                    <button
                      pButton
                      size="small"
                      class="compact-button"
                      (click)="saveReference(pageData.quote)"
                      [disabled]="
                        readOnly ||
                        isReferenceUnchanged(pageData.quote) ||
                        isSavingReference
                      "
                      [loading]="isSavingReference"
                    >
                      <span pButtonLabel>Save</span>
                    </button>
                    <button
                      pButton
                      size="small"
                      severity="secondary"
                      class="compact-button"
                      (click)="cancelReferenceEdit(pageData.quote)"
                      [disabled]="isSavingReference"
                    >
                      <span pButtonLabel>Cancel</span>
                    </button>
                  </div>
                </div>
              } @else {
                <div class="text-sm text-surface-switch-500">
                  {{ pageData.quote.reference || "—" }}
                </div>
              }
            </div>

            <div>
              <div class="text-xs text-surface-switch-600 mb-2">Total</div>
              <div class="text-lg font-semibold text-surface-switch-900">
                {{
                  pageData.revision.total
                    | currency: pageData.revision.currency_code
                }}
              </div>
            </div>

            <div>
              <div class="text-xs text-surface-switch-600 mb-2">
                Quote Created
              </div>
              <div class="text-sm text-surface-switch-900">
                {{ pageData.quote.created_date | date: "longDate" }}
              </div>
            </div>
            <div>
              <div class="text-xs text-surface-switch-600 mb-2">Created By</div>
              <div class="text-sm text-surface-switch-900">
                {{ pageData.quote.creator_email || "—" }}
              </div>
            </div>
            <div>
              <div class="flex items-center gap-1 text-sm mb-1">
                Customer
                <i
                  class="pi pi-info-circle text-primary"
                  pTooltip="New customers can be created on the Customers page"
                  tooltipPosition="top"
                ></i>
              </div>
              @if (companies$ | async; as companies) {
                <p-select
                  [options]="companies"
                  optionLabel="name"
                  optionValue="id"
                  [ngModel]="pageData.quote.company"
                  (onChange)="onCompanyChange($event.value, pageData.quote)"
                  [disabled]="readOnly || isSavingCompany"
                  [showClear]="true"
                  placeholder="Select customer"
                  appendTo="body"
                  size="small"
                  class="w-full @md:w-56 bg-transparent text-(--p-text-600) focus:outline-none focus:ring-0 shadow-none"
                  style="
                    --p-select-border-color: var(--color-surface-switch-300);
                    --p-select-focus-ring-shadow: none;
                    --p-select-focus-ring-width: 0px;
                    box-shadow: none;
                  "
                ></p-select>
              }
            </div>
            @if (!pageData.isApprovalFlowEnabled) {
              <div>
                <app-quote-status-select
                  [value]="pageData.quote.status"
                  [disabled]="readOnly"
                ></app-quote-status-select>
              </div>
            }
          </div>

          <p-divider class="my-6"></p-divider>

          <!-- Third section: Lead Time, Payment Terms, Notes -->
          <div class="grid grid-cols-1 @3xl:grid-cols-3 gap-6">
            <div>
              <div class="flex items-center gap-2 mb-3">
                <i class="pi pi-clock w-4 h-4 text-surface-switch-500"></i>
                <div class="text-xs text-surface-switch-600 font-semibold">
                  Lead Time
                </div>
                <button
                  pButton
                  [text]="true"
                  size="small"
                  severity="secondary"
                  [disabled]="readOnly"
                  (click)="editLeadTime(pageData.quote)"
                >
                  <i class="pi pi-pencil w-3 h-3"></i>
                </button>
              </div>
              <div class="text-sm text-surface-switch-500">
                @if (pageData.quote.lead_time_weeks != null) {
                  {{
                    weekLabels[pageData.quote.lead_time_weeks] ||
                      pageData.quote.lead_time_weeks
                  }}
                } @else if (pageData.quote.lead_time_calendar != null) {
                  {{ pageData.quote.lead_time_calendar | date: "longDate" }}
                } @else if (pageData.quote.lead_time_general) {
                  {{ pageData.quote.lead_time_general }}
                } @else {
                  —
                }
              </div>
            </div>
            <div>
              <div class="flex items-center justify-between gap-2 mb-3">
                <div class="flex items-center gap-2">
                  <i
                    class="pi pi-credit-card w-4 h-4 text-surface-switch-500"
                  ></i>
                  <div class="text-xs text-surface-switch-600 font-semibold">
                    Payment Terms
                  </div>
                  <button
                    pButton
                    [text]="true"
                    size="small"
                    severity="secondary"
                    [disabled]="readOnly"
                    (click)="openDrawer('payment_terms')"
                  >
                    <i class="pi pi-pencil w-3 h-3"></i>
                  </button>
                </div>
                @if (pageData.canUseStandardPaymentTerms) {
                  <button
                    pButton
                    [loading]="isSavingPaymentTerms"
                    [text]="true"
                    size="small"
                    severity="primary"
                    [disabled]="readOnly || isSavingPaymentTerms"
                    (click)="useStandardPaymentTerms(pageData.quote)"
                    label="Use Standard Terms"
                  ></button>
                } @else if (pageData.canAddStandardPaymentTerms) {
                  <a
                    pButton
                    [text]="true"
                    size="small"
                    severity="primary"
                    [routerLink]="['/settings', 'pdf-branding']"
                    fragment="payment-terms"
                    label="Add Standard Terms"
                  ></a>
                }
              </div>
              @if (
                pageData.quote.payment_terms_delta | hasDisplayableQuillContent
              ) {
                <app-quill-readonly
                  [content]="pageData.quote.payment_terms_delta"
                ></app-quill-readonly>
              } @else {
                <div class="text-sm text-surface-switch-500">—</div>
              }
            </div>
            <div>
              <div class="flex items-center gap-2 mb-3">
                <i class="pi pi-file-edit w-4 h-4 text-surface-switch-500"></i>
                <div class="text-xs text-surface-switch-600 font-semibold">
                  Notes
                </div>
                <button
                  pButton
                  [text]="true"
                  size="small"
                  severity="secondary"
                  [disabled]="readOnly"
                  (click)="openDrawer('notes')"
                >
                  <i class="pi pi-pencil w-3 h-3"></i>
                </button>
              </div>
              @if (pageData.quote.notes_delta | hasDisplayableQuillContent) {
                <app-quill-readonly
                  [content]="pageData.quote.notes_delta"
                ></app-quill-readonly>
              } @else {
                <div class="text-sm text-surface-switch-500">—</div>
              }
            </div>
          </div>
        </div>
      </div>
    </p-card>

    <div class="card flex justify-center">
      <p-drawer
        [(visible)]="isDrawerVisible"
        [header]="drawerHeader"
        position="right"
        [styleClass]="
          drawerContent !== 'lead_time' ? '!w-full @3xl:!w-3/4 @5xl:!w-1/2' : ''
        "
      >
        @if (drawerContent === "lead_time") {
          <div class="flex flex-col gap-3">
            <div class="flex items-center">
              <p-radioButton
                name="leadTime"
                value="general"
                [(ngModel)]="selectedLeadTime"
                inputId="general"
              />
              <label for="general" class="ml-2">General</label>
            </div>
            <div
              class="ml-6"
              [style.opacity]="selectedLeadTime !== 'general' ? '0.5' : '1'"
            >
              <textarea
                pTextarea
                [(ngModel)]="generalLeadTimeText"
                [rows]="3"
                [disabled]="selectedLeadTime !== 'general'"
                class="w-full"
              ></textarea>
            </div>
            <div class="flex items-center">
              <p-radioButton
                name="leadTime"
                value="calendar"
                [(ngModel)]="selectedLeadTime"
                inputId="calendar"
              />
              <label for="calendar" class="ml-2">Calendar</label>
            </div>
            <div
              class="ml-6"
              [style.opacity]="selectedLeadTime !== 'calendar' ? '0.5' : '1'"
            >
              <p-datepicker
                [(ngModel)]="calendarLeadTimeDate"
                dateFormat="dd/mm/yy"
                [showIcon]="true"
                [disabled]="selectedLeadTime !== 'calendar'"
                class="w-full"
              ></p-datepicker>
            </div>
            <div class="flex items-center">
              <p-radioButton
                name="leadTime"
                value="weeks"
                [(ngModel)]="selectedLeadTime"
                inputId="weeks"
              />
              <label for="weeks" class="ml-2">Weeks</label>
            </div>
            <div
              class="ml-6"
              [style.opacity]="selectedLeadTime !== 'weeks' ? '0.5' : '1'"
            >
              <p-select
                [(ngModel)]="selectedWeekOption"
                [options]="weekOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Select weeks"
                [disabled]="selectedLeadTime !== 'weeks'"
                class="w-full"
              ></p-select>
            </div>
            <div class="pt-2">
              <p-button
                (click)="saveLeadTime(pageData.quote)"
                [loading]="isSavingLeadTime"
                [disabled]="isSavingLeadTime || readOnly"
              >
                <span pButtonLabel>Save</span>
              </p-button>
            </div>
          </div>
        } @else {
          @defer (
            when drawerContent === 'payment_terms' || drawerContent === 'notes'
          ) {
            <div
              class="quill-container"
              style="height: 75vh; display: flex; flex-direction: column"
            >
              @if (isDrawerVisible) {
                <app-quill
                  [content]="
                    drawerContent === 'payment_terms'
                      ? pageData.quote.payment_terms_delta
                      : pageData.quote.notes_delta
                  "
                ></app-quill>
              }
            </div>
            <div class="pt-2">
              <p-button
                (click)="saveEditorContent(pageData.quote)"
                [loading]="isSavingEditor"
                [disabled]="isSavingEditor || readOnly"
              >
                <span pButtonLabel>Save</span>
              </p-button>
            </div>
          } @loading (after 100ms; minimum 1s) {
            <p-progressbar mode="indeterminate" [style]="{ height: '6px' }" />
          }
        }
      </p-drawer>
    </div>
  }
}
